import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Avatar,
    IconButton,
    Pagination,
    CircularProgress,
    Alert,
    Skeleton
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Business as BusinessIcon,
    YouTube as YouTubeIcon,
    OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import apiService from '../services/api';
import { useFilterStore } from '../stores/filterStore';

const BrandDetail: React.FC = () => {
    const { brandId } = useParams<{ brandId: string }>();
    const navigate = useNavigate();
    const { timePeriod } = useFilterStore();
    const [page, setPage] = useState(1);
    const limit = 50;

    // Fetch brand details
    const { data: brandData, isLoading: brandLoading, error: brandError } = useQuery({
        queryKey: ['brand', brandId],
        queryFn: () => apiService.getBrand(brandId!),
        enabled: !!brandId
    });

    // Fetch brand mentions
    const { data: mentionsData, isLoading: mentionsLoading, error: mentionsError } = useQuery({
        queryKey: ['brand-mentions', brandId, page, timePeriod],
        queryFn: () => apiService.getBrandMentions(brandId!, page, limit, undefined, timePeriod),
        enabled: !!brandId
    });

    const brand = brandData?.brand;
    const mentions = mentionsData?.mentions || [];
    const pagination = mentionsData?.pagination;
    const totalPages = pagination?.pages || 1;

    const handleBack = () => {
        navigate('/review');
    };

    const formatDuration = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const getClassificationColor = (classification: string) => {
        switch (classification) {
            case 'paid': return 'success';
            case 'organic': return 'primary';
            case 'neutral': return 'default';
            default: return 'default';
        }
    };

    const generateYouTubeUrl = (url: string, startTime: number) => {
        if (!url) return null;

        // Check if it's a YouTube URL
        if (url.includes('youtube.com') || url.includes('youtu.be')) {
            const urlObj = new URL(url);
            urlObj.searchParams.set('t', Math.floor(startTime).toString());
            return urlObj.toString();
        }

        return null;
    };

    const handleYouTubeClick = (url: string, startTime: number) => {
        const youtubeUrl = generateYouTubeUrl(url, startTime);
        if (youtubeUrl) {
            window.open(youtubeUrl, '_blank');
        }
    };

    if (brandError || mentionsError) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Alert severity="error">
                    Failed to load brand details
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            {/* Header */}
            <Box display="flex" alignItems="center" mb={3}>
                <IconButton onClick={handleBack} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" component="h1">
                    Brand Details
                </Typography>
            </Box>

            {/* Brand Info Card */}
            <Card sx={{ mb: 4 }}>
                <CardContent>
                    {brandLoading ? (
                        <Box display="flex" alignItems="center" gap={3}>
                            <Skeleton variant="circular" width={80} height={80} />
                            <Box>
                                <Skeleton variant="text" width={200} height={32} />
                                <Skeleton variant="text" width={150} height={24} />
                                <Skeleton variant="text" width={100} height={20} />
                            </Box>
                        </Box>
                    ) : brand ? (
                        <Box display="flex" alignItems="center" gap={3}>
                            <Avatar
                                sx={{
                                    width: 80,
                                    height: 80,
                                    bgcolor: 'primary.main',
                                    fontSize: '2rem',
                                    fontWeight: 'bold'
                                }}
                            >
                                {brand.name ? brand.name.charAt(0).toUpperCase() : <BusinessIcon />}
                            </Avatar>
                            <Box>
                                <Typography variant="h5" gutterBottom>
                                    {brand.name}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" gutterBottom>
                                    {brand.category || 'Uncategorized'}
                                </Typography>
                                {brand.aliases && brand.aliases.length > 0 && (
                                    <Typography variant="body2" color="text.secondary">
                                        Aliases: {brand.aliases.join(', ')}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    ) : null}
                </CardContent>
            </Card>

            {/* Mentions Table */}
            <Card>
                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                        <Typography variant="h6">
                            Brand Mentions
                            {timePeriod !== 'all' && (
                                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                    - {timePeriod.charAt(0).toUpperCase() + timePeriod.slice(1)}
                                </Typography>
                            )}
                        </Typography>
                        {pagination && (
                            <Typography variant="body2" color="text.secondary">
                                {pagination.total} total mentions
                            </Typography>
                        )}
                    </Box>

                    {mentionsLoading ? (
                        <Box display="flex" justifyContent="center" p={4}>
                            <CircularProgress />
                        </Box>
                    ) : mentions.length === 0 ? (
                        <Alert severity="info">
                            No mentions found for this brand in the selected time period.
                        </Alert>
                    ) : (
                        <>
                            <TableContainer component={Paper} variant="outlined">
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Content</TableCell>
                                            <TableCell align="center">Podcast</TableCell>
                                            <TableCell align="center">Time</TableCell>
                                            <TableCell align="center">Duration</TableCell>
                                            <TableCell align="center">Classification</TableCell>
                                            <TableCell align="center">Confidence</TableCell>
                                            <TableCell align="center">Date</TableCell>
                                            <TableCell align="center">Link</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {mentions
                                            .sort((a: any, b: any) => a.startTime - b.startTime) // Sort by start time (earliest to latest)
                                            .map((mention) => (
                                                <TableRow key={mention._id} hover>
                                                    <TableCell>
                                                        <Box>
                                                            <Typography variant="subtitle2" noWrap sx={{ maxWidth: 300 }}>
                                                                {mention.content?.title || 'Unknown Content'}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {mention.context}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                                                            <Avatar
                                                                src={mention.content?.podcast?.imageUrl}
                                                                sx={{ width: 24, height: 24 }}
                                                            />
                                                            <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                                                {mention.content?.podcast?.title || 'Unknown Podcast'}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="body2">
                                                            {formatDuration(mention.startTime)} - {formatDuration(mention.endTime)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="body2">
                                                            {formatDuration(mention.duration)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={mention.classification}
                                                            size="small"
                                                            color={getClassificationColor(mention.classification)}
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="body2">
                                                            {Math.round(mention.confidence * 100)}%
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="caption" color="text.secondary">
                                                            {mention.content?.publishedAt
                                                                ? new Date(mention.content.publishedAt).toLocaleDateString()
                                                                : 'Unknown'
                                                            }
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {mention.content?.url && generateYouTubeUrl(mention.content.url, mention.startTime) ? (
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleYouTubeClick(mention.content!.url!, mention.startTime)}
                                                                sx={{ color: '#FF0000' }}
                                                                title="Watch on YouTube"
                                                            >
                                                                <YouTubeIcon />
                                                            </IconButton>
                                                        ) : mention.content?.url ? (
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => window.open(mention.content!.url!, '_blank')}
                                                                title="Open content"
                                                            >
                                                                <OpenInNewIcon />
                                                            </IconButton>
                                                        ) : (
                                                            <Typography variant="caption" color="text.disabled">
                                                                No link
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <Box display="flex" justifyContent="center" mt={3}>
                                    <Pagination
                                        count={totalPages}
                                        page={page}
                                        onChange={(_, newPage) => setPage(newPage)}
                                        color="primary"
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>
        </Container>
    );
};

export default BrandDetail;
